import { useCallback, useEffect, useState } from 'react';
import { useDiffApiFacade } from '../../../../../apis/facades/useDiffApiFacade';
import { Box } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { ArrowRight } from '@sweep-io/sweep-design/dist/icons';
import { CenteredCircularProgress } from '../../../../common/CenteredCircularProgress';
import { TextWithMentions } from '../../../../common/comments/TextWithMentions';
import { DataTable } from '../../../../common/table/DataTable';
import {
  DataTableBaseColumn,
  DataTableRow,
  DataTableVariant,
} from '../../../../common/table/TableTypes';
import { DataTableId } from '../../../../common/table/utils';

type DiffItem = { message: string; before: string; after: string };

export const DeploymentDiff = ({
  snapshotId,
  funnelId,
  crmOrgId,
}: {
  snapshotId?: string;
  funnelId?: string;
  crmOrgId?: string;
}) => {
  const { get_diffBySnapshotId, get_diffByFunnelId } = useDiffApiFacade();
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [diffResults, setDiffResults] = useState<DiffItem[]>([]);

  const fetchDiff = useCallback(() => {
    if (snapshotId && crmOrgId) {
      return get_diffBySnapshotId({ snapshotId, crmOrgId });
    }
    if (funnelId && crmOrgId) {
      return get_diffByFunnelId({ funnelId, crmOrgId });
    }
  }, [crmOrgId, funnelId, get_diffByFunnelId, get_diffBySnapshotId, snapshotId]);

  const fetchSuccess = (res: DiffItem[]) => {
    setIsLoading(false);
    setDiffResults(res);
  };
  const fetchError = () => {
    setIsLoading(false);
    setIsError(true);
  };

  useEffect(() => {
    fetchDiff()?.then(fetchSuccess, fetchError);
  }, [fetchDiff]);

  if (isLoading) {
    return (
      <Box m={2}>
        <CenteredCircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box mt={1}>
        <Typography variant="caption-bold" color={colors.blush[500]}>
          Sorry, we could not calculate the difference between current and deployed version
        </Typography>
      </Box>
    );
  }

  if (diffResults.length === 0) {
    return (
      <Box mt={1}>
        <Typography variant="caption-bold" color={colors.grey[800]}>
          No changes were identified
        </Typography>
      </Box>
    );
  }

  const columns: DataTableBaseColumn[] = [
    { field: 'index', headerName: '#', width: '60px' },
    { field: 'message', headerName: 'Change', width: '40%' },
    { field: 'before', headerName: 'Current configuration', width: '25%' },
    { field: 'arrow', headerName: '', width: '10px' },
    { field: 'after', headerName: 'Requested changes' },
  ];

  const rows: DataTableRow[] = diffResults.map(({ message, before, after }, idx) => ({
    id: `diff-${idx}`,
    index: idx + 1,
    message: <TextWithMentions text={message} highlightBold highlightColor={colors.grey[900]} />,
    before: <TextWithMentions text={before} highlightBold highlightColor={colors.grey[900]} />,
    arrow: before && after ? <ArrowRight size="medium" /> : null,
    after: <TextWithMentions text={after} highlightBold highlightColor={colors.grey[900]} />,
  }));

  return (
    <Box sx={{ minWidth: '1218px' }}>
      <DataTable
        tableId={'deployment-diff-table' as DataTableId}
        columns={columns}
        rows={rows}
        variant={DataTableVariant.small}
      />
    </Box>
  );
};
